import React, { useContext, useEffect, useState } from 'react';
import './index.scss';
import { getAPICall, postAPICall } from '../../dataFetcher/axiosMethodCalls';
import { Field } from '@sitecore-jss/sitecore-jss-react';
import AddressListPopup from './AddressListPopup';
import AddNewAddress from '../AddNewAddress/AddNewAddress';
import { APIWebstoreContext } from '../../context/globalContext';
import LoadingSkeleton from '../LoadingSkeleton/LoadingSkeleton';
import { ProcessOrderValidationService } from '../CheckoutPage/ProcessOrderValidationService';
import { NO_DIGITAL_DELIVERY } from '../../utils/constants';
import BillingAddress from '../PaymentMethod/BillingAddress';

export interface ShippingAddressDetails {
  UserId: string;
  AddressId: string;
  FirstName: string;
  LastName: string;
  Street1: string;
  Street2: string;
  City: string;
  State: string;
  Zip: string;
  Country: string;
  Phone: string;
  IsShipping: boolean;
  IsBilling: boolean;
  IsPrimary: boolean;
}
export interface IShippingAddressDetails {
  userId: string;
  addressId: string;
  firstName: string;
  lastName: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone: string;
  isShipping: boolean;
  isBilling: boolean;
  isPrimary: boolean;
  shipToChangeAllowed: boolean;
}
export const shippingInformation: IShippingAddressDetails = {
  userId: '',
  addressId: '',
  firstName: '',
  lastName: '',
  street1: '',
  street2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  phone: '',
  isShipping: false,
  isBilling: false,
  isPrimary: false,
  shipToChangeAllowed: true,
};

export type ShippingInformationProps = {
  fields: {
    ChangeLink: Field<string>;
    SavedToastMessage?: Field<string>;
    ButtonText?: Field<string>;
    AddressNote?: Field<string>;
    BillingAddressInfo?: Field<string>;
  };
};

export interface ICountry {
  country: string;
  countryCode: string;
}

const ShippingInformation = ({ fields }: ShippingInformationProps): JSX.Element => {
  const [countryList, setCountryList] = useState(Array<ICountry>());
  const [openAddressListPopup, setOpenAddressListPopup] = useState(false);
  const [openAddAddressPopup, setOpenAddAddressPopup] = useState(false);
  const { state, dispatch } = useContext(APIWebstoreContext);
  const [shippingAddress, setShippingAddress] = useState<IShippingAddressDetails>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getOrderShippingAddressApiCall();
  }, []);

  function getOrderShippingAddressApiCall() {
    getAPICall(`${process.env.REACT_APP_CART_URL}/checkout/shippingAddress`)
      .then(function (response: any) {
        setShippingAddressFromOrderData(response.data);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  function setShippingAddressFromOrderData(address: IShippingAddressDetails) {
    postAPICall(`${process.env.REACT_APP_CART_URL}/checkout/setshippingaddress`, address).then(
      function (response: any) {
        if (response && response.data) {
          setShippingAddress(response.data);
          ProcessOrderValidationService.setShippingInfo(response.data, false);
          if (dispatch) {
            dispatch({
              type: 'CHECKOUT_UPDATE',
              payload: {
                toggleCalculateOrder: !state?.toggleCalculateOrder,
                shipToChangeAllowed: response.data.shipToChangeAllowed,
              },
            });
          }
        }
      }
    );
  }

  function getCountryName(countryCode: string) {
    let country = countryList.filter((x) => x.countryCode === countryCode);
    if (country.length > 0) {
      return country[0].country;
    }
    return countryCode;
  }

  const onOpenAddressListPopup = () => {
    setOpenAddressListPopup(true);
  };

  const onCloseAddressListPopup = (addr: IShippingAddressDetails, save: boolean) => {
    if (save && addr !== undefined) {
      setShippingAddressFromOrderData(addr);
    }
    setOpenAddressListPopup(false);
  };

  const onCloseAddressPopup = (addressCreated: boolean) => {
    setOpenAddAddressPopup(false);
    addressCreated && getOrderShippingAddressApiCall();
  };
  const onOpenAddressPopup = () => {
    setOpenAddAddressPopup(true);
  };

  const addNewAddress = () => {
    onOpenAddressPopup();
  };

  function RenderShippingInformation() {
    return (
      <div className="maindiv">
        {isLoading && (
          <>
            <span className="shippinginfoheader">
              <div className="shippingTitle">
                Shipping Information
                <label className="shippinginfoheaderdetail">
                  (this is required to calculate tax)
                </label>
              </div>
              <LoadingSkeleton theme="light" />
            </span>
            <br />
          </>
        )}
        {!isLoading && shippingAddress && (
          <>
            <span className="shippinginfoheader">
              <div className="shippingTitle">
                Shipping Information
                <label className="shippinginfoheaderdetail">
                  (this is required to calculate tax)
                </label>
              </div>
              {shippingAddress && shippingAddress.shipToChangeAllowed && (
                <>
                  <a href="#" className="changelink" onClick={onOpenAddressListPopup}>
                    {fields.ChangeLink?.value}
                  </a>
                </>
              )}
            </span>
            <p className="address-note">
              <b>Note:</b> {fields?.AddressNote?.value}
            </p>
            <div className="countrydiv">
              <span className="addressinfoheader">Country</span>
              <br></br>
              {countryList && (
                <>
                  <span className="inputtext">{getCountryName(shippingAddress.country)}</span>
                  <br />
                </>
              )}
            </div>
            <br />
            {shippingAddress.firstName && shippingAddress.lastName && (
              <div className="namediv">
                <div className="firstnamediv">
                  <span className="addressinfoheader">First Name</span>
                  <br></br>
                  <span className="inputtext">{shippingAddress.firstName}</span>
                </div>
                <div className="lastnamediv">
                  <span className="addressinfoheader">Last Name</span>
                  <br />
                  <span className="inputtext">{shippingAddress.lastName}</span>
                </div>
                <br />
              </div>
            )}
            <div className="streetdiv">
              <span className="addressinfoheader">Street Address or P.O. Box</span>
              <span className="po-disclaimer">
                {state?.sitecoreDictionaryData
                  ? ` (${state?.sitecoreDictionaryData.POBoxDisclaimerText})`
                  : ' (P.O. Box Address is not accepted for print orders)'}
              </span>
              <br></br>
              <span className="inputtext">{shippingAddress.street1}</span>
            </div>
            <br />
            <div className="alignAddress">
              <div className="unitdiv">
                <span className="addressinfoheader">Apt, Suite, Unit, Building, Floor, etc.</span>
                <br></br>
                {shippingAddress.street2 !== '' && (
                  <span className="inputtext">{shippingAddress.street2}</span>
                )}
                {shippingAddress.street2 === null && <span className="inputtext">-</span>}
              </div>
              <div className="citydiv">
                <span className="addressinfoheader">City</span>
                <br></br>
                <span className="inputtext">{shippingAddress.city}</span>
              </div>
              <div className="stdiv">
                <span className="addressinfoheader">State/ Province</span>
                <br></br>
                <span className="inputtext">{shippingAddress.state}</span>
              </div>
              <div className="postaldiv">
                <span className="addressinfoheader">Postal Code</span>
                <br></br>
                <span className="inputtext">{shippingAddress.zip}</span>
              </div>
            </div>
            <br />
            <div className="phonediv">
              <span className="addressinfoheader">Phone Number</span>
              <br></br>
              <span className="inputtext">{shippingAddress.phone}</span>
            </div>
          </>
        )}
        {!isLoading && shippingAddress === undefined && (
          <div>
            <span className="shippinginfoheader">
              <div className="shippingTitle">
                Shipping Information
                <label className="shippinginfoheaderdetail">
                  (this is required to calculate tax)
                </label>
              </div>
            </span>
            <button className="addnewaddressbtn" onClick={addNewAddress}>
              ADD NEW ADDRESS
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="shippinginformation">
        {state?.deliveryStatus === NO_DIGITAL_DELIVERY && (
          <div className="reviewOrderSection">
            <div className="reviewOrderSNo">1</div>
          </div>
        )}
        <div
          className={`shippingContainer ${
            state?.deliveryStatus !== NO_DIGITAL_DELIVERY ? 'has-digital' : ''
          }`}
        >
          {RenderShippingInformation()}
        </div>
        <AddressListPopup
          props={{
            open: openAddressListPopup,
            closeAddressListPopup: onCloseAddressListPopup,
            selectAddressId: shippingAddress?.addressId,
            countryList: countryList,
            openAddNewAddressPopup: onOpenAddressPopup,
          }}
        ></AddressListPopup>
        <AddNewAddress
          open={openAddAddressPopup}
          closeAddressPopup={onCloseAddressPopup}
          editMode={false}
          fields={{
            SavedToastMessage: fields.SavedToastMessage,
            ButtonText: fields.ButtonText,
          }}
        ></AddNewAddress>
      </div>
      <div className="billinginformation">
        {!isLoading && (
          <>
            <div className="billinginformationheader">Billing Information</div>
            <BillingAddress
              fields={{
                BillingAddressInfo:
                  'If you are using a credit card with a billing address outside of the United States you may be subject to additional fees. Please check with your payment provider for further information or if you have any questions.',
              }} // have to update the field after configuring it from sitecore
            ></BillingAddress>
          </>
        )}
      </div>
    </>
  );
};

export default ShippingInformation;
